import { Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ToDatePipe } from '../../../../../@common/pipe/to-date.pipe';
import { PlaylistInfo } from '../../../../../@generated';
import { AppRoute } from '../../../../../routing/types/app-route';
import { ImageDirective } from 'src/app/@common/DOM/image.directive';
@Component({
    selector: 'app-playlist-item',
    imports: [RouterLink, ToDatePipe, ImageDirective],
    styles: `
        @use "variables" as *;

        /* ── Card (grid) view ───────────────────────────────── */

        .pl-card {
            display: block;
            width: 100%;
            text-decoration: none;
            color: inherit;
        }

        /*
         * 3-layer stacking effect. All layers share the same bottom edge.
         * Each layer is 7px lower and 7px less inset than the one behind it.
         *
         *   .pl-back   top: 0,  left/right: 21px  (furthest, darkest)
         *   ::before   top: 7px, left/right: 14px  (middle)
         *   ::after    top: 14px, left/right: 7px  (closest)
         *   .thumb-front at padding-top: 21px       (front image)
         *
         * Fixed width = thumbnail width so back cards never overflow
         * the thumbnail and the body text stays perfectly left-aligned.
         */
        .pl-thumb {
            position: relative;
            padding-top: 21px;
            width: 256px;

            @media screen and (max-width: 768px) { width: 190px; }
            @media screen and (max-width: 450px) { width: 165px; }

            .pl-back {
                position: absolute;
                top: 0;
                left: 21px;
                right: 21px;
                bottom: 0;
                background: #aaaaaa;
                z-index: 0;
            }

            &::before {
                content: '';
                position: absolute;
                top: 7px;
                left: 14px;
                right: 14px;
                bottom: 0;
                background: #bdbdbd;
                z-index: 1;
            }

            &::after {
                content: '';
                position: absolute;
                top: 14px;
                left: 7px;
                right: 7px;
                bottom: 0;
                background: #d6d6d6;
                z-index: 2;
            }
        }

        /* Front image — identical dimensions to video-item (256×144) */
        .thumb-front {
            position: relative;
            z-index: 3;
            width: 100%;
            height: 144px;
            overflow: hidden;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            }

            @media screen and (max-width: 768px) { height: 107px; }
            @media screen and (max-width: 450px) { height: 85px; }
        }

        /* Body below the thumbnail — mirrors video-item card body */
        .pl-body {
            padding: 0.5rem 0 0;
            width: 256px;

            @media screen and (max-width: 768px) { width: 190px; }
            @media screen and (max-width: 450px) { width: 165px; }

            h3 {
                font-size: 16px;
                font-weight: 600;
                line-height: 20px;
                color: $dark-red;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                width: 100%;

                @media screen and (max-width: 450px) { font-size: 14px; }
            }

            .pl-meta {
                display: flex;
                align-items: center;
                gap: 20px;
                font-size: 13px;
                font-weight: 400;
                color: $dark-blue;
                line-height: 16px;

                @media screen and (max-width: 400px) {
                    gap: 10px;
                    font-size: 10px;
                }

                .meta-item {
                    display: flex;
                    align-items: center;
                    gap: 4px;

                    svg {
                        width: 18px;
                        height: 18px;
                        flex-shrink: 0;
                        color: $dark-blue;
                    }
                }
            }
        }

        /* ── List view (unchanged) ───────────────────────────── */

        .list-view {
            display: flex;
            align-items: center;
            margin-bottom: 1rem;
            padding: 0.5rem;
            cursor: pointer;
            text-decoration: none;
            color: inherit;

            img {
                height: 60px;
                width: 150px;
                margin-right: 1.5rem;
                object-fit: cover;
                flex-shrink: 0;
            }

            .right {
                display: flex;
                align-items: center;
                width: 100%;

                .info {
                    width: 60%;
                    strong { color: $dark; margin-right: 1.5rem; }
                }

                .numbers {
                    display: flex;
                    width: 40%;
                    p { width: 50%; }
                }

                @media screen and (max-width: 767px) {
                    flex-direction: column;
                    .info { width: 100%; margin-right: 0; }
                    .numbers {
                        width: 100%;
                        flex-direction: column;
                        p { width: fit-content; margin-right: 1rem; }
                    }
                }

                @media screen and (max-width: 600px) {
                    .info { font-size: 16px; }
                    .numbers p { font-size: 14px; }
                }
            }
        }
    `,
    template: `
        @if (!listView()) {
            <a class="pl-card" routerLink="/{{AppRoutes.Playlists}}/{{playlist().id}}">

                <div class="pl-thumb">
                    <div class="pl-back"></div>
                    <div class="thumb-front">
                        <img [src]="resolvedThumbnail()"
                             alt="playlist thumbnail"
                             loading="lazy"/>
                    </div>
                </div>

                <div class="pl-body">
                    <h3 [title]="playlist().title">{{ playlist().title }}</h3>
                    <div class="pl-meta">
                        <span class="meta-item">
                            <img src="/assets/icons/calendar.svg" alt="calendar icon" />
                            {{ playlist().created | toDate }}
                        </span>
                        <span class="meta-item">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <line x1="8" y1="6" x2="21" y2="6"/>
                                <line x1="8" y1="12" x2="21" y2="12"/>
                                <line x1="8" y1="18" x2="21" y2="18"/>
                                <line x1="3" y1="6" x2="3.01" y2="6"/>
                                <line x1="3" y1="12" x2="3.01" y2="12"/>
                                <line x1="3" y1="18" x2="3.01" y2="18"/>
                            </svg>
                            {{ playlist().total_videos }} videos
                        </span>
                    </div>
                </div>

            </a>
        } @else {
            <a class="list-view" routerLink="/{{AppRoutes.Playlists}}/{{playlist().id}}">
                <img  [src]="resolvedThumbnail()" alt="playlist thumbnail" loading="lazy"/>
                <div class="right">
                    <div class="info">
                        <strong>{{ playlist().title }}</strong>
                    </div>
                    <div class="numbers">
                        <p>Updated {{ playlist().created | toDate }}</p>
                        <p>{{ playlist().total_videos }} Videos</p>
                    </div>
                </div>
            </a>
        }
    `
})
export class PlaylistItemComponent {
    public playlist = input.required<PlaylistInfo>();
    public listView = input.required<boolean>();
    public thumbnailUrl = input<string | undefined>(undefined);
    public AppRoutes = AppRoute;

    public resolvedThumbnail = computed(() =>
        this.thumbnailUrl() || this.playlist().thumbnail || 'assets/download.png'
    );
}
